import { flexRender } from '@tanstack/react-table';
import { Loader2Icon } from 'lucide-react';

import {
  formatMobileTableColumnLabel,
  getMobileTableSelectHeader,
  isMobileTableUtilityColumn,
  pickMobileTableTitleColumnId,
  shouldShowMobileTableDetailCell,
} from './mobile-table-cards.helpers';
import { MOBILE_TABLE_UTILITY_COLUMN_ID } from './mobile-table-cards.types';
import type {
  MobileTableCardListProps,
  MobileTableCardProps,
} from './mobile-table-cards.types';

export function MobileTableCard<TData>({ row }: MobileTableCardProps<TData>) {
  const cells = row.getVisibleCells();
  const selectCell = cells.find(
    (cell) =>
      cell.column.id.toLowerCase() === MOBILE_TABLE_UTILITY_COLUMN_ID.SELECT,
  );
  const actionCell = cells.find(
    (cell) =>
      isMobileTableUtilityColumn(cell.column.id) &&
      cell.column.id.toLowerCase() !== MOBILE_TABLE_UTILITY_COLUMN_ID.SELECT,
  );
  const dataCells = cells.filter(
    (cell) => !isMobileTableUtilityColumn(cell.column.id),
  );
  const titleColumnId = pickMobileTableTitleColumnId(
    dataCells.map((cell) => cell.column.id),
  );
  const titleCell = dataCells.find((cell) => cell.column.id === titleColumnId);
  const visibleDetailCells = dataCells.filter((cell) => {
    if (cell.column.id === titleColumnId) {
      return false;
    }

    return shouldShowMobileTableDetailCell(cell.column.id, cell.getValue());
  });

  return (
    <article className='rounded-xl border border-border/50 p-3.5'>
      <div className='mb-2 flex items-start gap-2'>
        <div className='min-w-0 flex-1'>
          {titleCell ? (
            <div className='break-words text-sm font-semibold leading-5 text-foreground [&_a]:whitespace-normal [&_a]:p-0 [&_a]:text-sm [&_a]:font-semibold [&_button]:h-auto [&_button]:whitespace-normal [&_button]:p-0 [&_button]:text-sm [&_button]:font-semibold [&_div]:px-0'>
              {flexRender(
                titleCell.column.columnDef.cell,
                titleCell.getContext(),
              )}
            </div>
          ) : null}
        </div>
        {actionCell ? (
          <div className='shrink-0 [&_div]:px-0'>
            {flexRender(
              actionCell.column.columnDef.cell,
              actionCell.getContext(),
            )}
          </div>
        ) : null}
        {selectCell ? (
          <div className='shrink-0 [&_div]:px-0'>
            {flexRender(
              selectCell.column.columnDef.cell,
              selectCell.getContext(),
            )}
          </div>
        ) : null}
      </div>
      {visibleDetailCells.length > 0 ? (
        <dl className='space-y-1.5'>
          {visibleDetailCells.map((cell) => {
            const label = formatMobileTableColumnLabel(cell.column.id);
            return (
              <div
                key={cell.id}
                className='flex items-start justify-between gap-3'
              >
                {label ? (
                  <dt className='w-[38%] shrink-0 text-sm leading-5 text-muted-foreground'>
                    {label}
                  </dt>
                ) : null}
                <dd className='flex min-w-0 flex-1 flex-col items-end break-words text-right text-sm font-normal leading-5 text-foreground [overflow-wrap:anywhere] [&_a]:p-0 [&_button]:h-auto [&_button]:p-0 [&_div]:ml-0 [&_div]:flex-wrap [&_div]:justify-end [&_div]:px-0'>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}
    </article>
  );
}

export function MobileTableCardList<TData>({
  table,
  isUpdating,
}: MobileTableCardListProps<TData>) {
  const rows = table.getRowModel().rows;
  const selectHeader = getMobileTableSelectHeader(table);

  if (isUpdating && rows.length === 0) {
    return (
      <div className='flex items-center justify-center py-8 md:hidden'>
        <Loader2Icon className='size-8 animate-spin' />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className='py-8 text-center text-sm text-muted-foreground md:hidden'>
        No results.
      </p>
    );
  }

  return (
    <div className='relative space-y-3 md:hidden'>
      {selectHeader && !selectHeader.isPlaceholder ? (
        <div className='flex items-center gap-2 rounded-xl border border-border/50 px-3.5 py-2.5'>
          <div className='[&_div]:px-0'>
            {flexRender(
              selectHeader.column.columnDef.header,
              selectHeader.getContext(),
            )}
          </div>
          <span className='text-sm font-medium'>Select all</span>
        </div>
      ) : null}
      {rows.map((row) => (
        <MobileTableCard key={row.id} row={row} />
      ))}
      {isUpdating ? (
        <div className='absolute inset-0 flex items-center justify-center bg-background/50'>
          <Loader2Icon className='size-8 animate-spin' />
        </div>
      ) : null}
    </div>
  );
}
